'use client';

import { useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export const ScrollWithGradient = ({
  children,
  className,
  gradientColor = 'from-neutral-950',
  gradientWidth = 'w-8',
}: {
  children: ReactNode;
  className?: string;
  gradientColor?: string;
  gradientWidth?: string;
}) => {
  const [showRightGradient, setShowRightGradient] = useState(true);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollWidth, scrollLeft, clientWidth } = scrollContainerRef.current;
    const isAtEnd = Math.abs(scrollWidth - clientWidth - scrollLeft) <= 1;
    const isAtStart = scrollLeft <= 1;

    setShowRightGradient(!isAtEnd);
    setShowLeftGradient(!isAtStart);
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className={twMerge('overflow-x-auto scrollbar-hide', className)}>
        {children}
      </div>
      {showLeftGradient && (
        <div
          className={twMerge(
            'absolute left-[-1px] top-0 h-full bg-gradient-to-r to-transparent pointer-events-none',
            gradientColor,
            gradientWidth
          )}
        />
      )}
      {showRightGradient && (
        <div
          className={twMerge(
            'absolute right-[-1px] top-0 h-full bg-gradient-to-l to-transparent pointer-events-none',
            gradientColor,
            gradientWidth
          )}
        />
      )}
    </div>
  );
};
